import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import OwnPropertyNotice from "../components/OwnPropertyNotice";

const images = [
  "/images/company-hero1.jpg",
  "/images/company-hero2.jpg",
  "/images/company-hero3.jpg",
];

// 事業カードの内容
const businesses = [
  {
    to: "/businessContent/minpaku",
    en: "STAY",
    title: "民泊運営事業",
    desc: "河口湖・富士吉田を中心に、富士山周辺で快適にご滞在いただける宿泊施設を運営しています。",
  },
  {
    to: "/businessContent/real-estate",
    en: "REAL ESTATE",
    title: "不動産事業",
    desc: "富士山周辺エリアの土地・戸建・マンション等の売買物件をご紹介しています。",
  },
];

// セクション見出し（番号＋英語＋日本語）
const SectionHeading = ({ no, en, jp }) => (
  <div className="mb-10">
    <div className="flex items-baseline gap-3">
      <span className="text-primary font-bold tracking-widest">{no}</span>
      <span className="text-sm tracking-[0.2em] text-gray-400 font-medium">
        {en}
      </span>
    </div>
    <h2 className="mt-1 text-2xl md:text-3xl font-bold text-gray-800">{jp}</h2>
    <div className="mt-3 h-0.5 w-12 bg-primary" />
  </div>
);

const Top = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [latestNews, setLatestNews] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        setFade(true);
      }, 1000);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchLatestNews = async () => {
      try {
        const res = await fetch("/api/public/news?limit=5");
        const result = await res.json();
        if (Array.isArray(result.data)) {
          setLatestNews(result.data);
        }
      } catch (err) {
        console.error("最新お知らせの取得に失敗:", err);
      }
    };

    fetchLatestNews();
  }, []);

  return (
    <div className="bg-white">
      {/* SEOのためのタイトル・meta */}
      <Helmet>
        <title>アプロ山梨株式会社</title>
        <meta
          name="description"
          content="確かな実績をもとに新たな展望へ導く不動産総合事業サービス。アプロ山梨株式会社の公式サイトです。"
        />
      </Helmet>

      {/* ヒーローセクション */}
      <div className="relative w-full h-[100svh] min-h-[520px] overflow-hidden">
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: fade ? 1 : 0 }}
        >
          <img
            src={images[currentImageIndex]}
            alt="会社イメージ"
            className="w-full h-full object-cover"
          />
        </div>
        {/* 可読性のためのオーバーレイ */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-white/80 text-xs md:text-sm tracking-[0.35em] mb-5">
            APRO YAMANASHI
          </p>
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight drop-shadow">
            確かな実績をもとに、
            <br />
            新たな展望へ。
          </h1>
          <p className="mt-5 text-white/90 text-sm md:text-lg tracking-wide">
            富士山エリアの不動産総合事業サービス
          </p>
        </div>

        {/* スクロール誘導 */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-widest animate-bounce">
          SCROLL
        </div>
      </div>

      {/* 事業内容 */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading no="01" en="BUSINESS" jp="事業内容" />

          <div className="grid gap-6 md:grid-cols-2">
            {businesses.map((b) => (
              <Link
                key={b.to}
                to={b.to}
                className="group block rounded-xl border border-gray-200 p-8 transition hover:shadow-lg hover:-translate-y-0.5 hover:border-primary"
              >
                <p className="text-xs tracking-[0.2em] text-gray-400">{b.en}</p>
                <h3 className="mt-2 text-2xl font-bold text-gray-800">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm leading-7 text-gray-600">{b.desc}</p>
                <span className="mt-4 inline-block text-primary font-medium group-hover:underline">
                  詳しく見る →
                </span>
              </Link>
            ))}
          </div>

          <OwnPropertyNotice className="mt-10" />
        </div>
      </section>

      {/* 最新のお知らせ */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading no="02" en="NEWS" jp="お知らせ" />

          <ul className="divide-y divide-gray-200 border-t border-gray-200">
            {latestNews.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/${item.id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 py-4 group"
                >
                  <span className="text-sm text-gray-500 shrink-0 w-32">
                    {new Date(
                      item.createdAt || item.updatedAt
                    ).toLocaleDateString("ja-JP", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                  <span className="text-gray-800 group-hover:text-primary transition">
                    {item.title}
                  </span>
                </Link>
              </li>
            ))}

            {latestNews.length === 0 && (
              <li className="py-6 text-gray-400 text-sm">読み込み中...</li>
            )}
          </ul>

          <div className="mt-8">
            <Link
              to="/news"
              className="text-primary font-medium hover:underline"
            >
              お知らせ一覧を見る →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Top;
